// /Users/shawn/💼_WORKSPACE/Projects/FixPriceAI-Desktop/src/services/SmartAIRouter.js
// Purpose: Desktop version of SmartAIRouter with Node.js compatibility
// NOT for: Mobile React Native - use original version for mobile

import axios from 'axios';

class SmartAIRouter {
  constructor() {
    this.models = {
      screening: 'moondream2',    // FREE - Ultra fast screening
      simple: 'llava',            // FREE - Good for basic problems
      complex: 'imagen3',         // PAID - Best for complex analysis
      fallback: 'gemini'          // PAID - Reliable fallback
    };

    this.costTracker = {
      daily: 0,
      monthly: 0,
      dailyLimit: 5,     // $5/day budget
      monthlyLimit: 100  // $100/month budget
    };

    this.performance = {
      moondream: { calls: 0, success: 0, avgTime: 0 },
      llava: { calls: 0, success: 0, avgTime: 0 },
      gemini: { calls: 0, success: 0, avgTime: 0 },
      imagen: { calls: 0, success: 0, avgTime: 0 }
    };

    this.loadCostTracker();
  }

  loadCostTracker() {
    try {
      // Check if localStorage is available (browser environment)
      if (typeof localStorage !== 'undefined') {
        const savedData = localStorage.getItem('ai_cost_tracker');
        if (savedData) {
          const data = JSON.parse(savedData);
          const today = new Date().toDateString();

          // Reset daily if new day
          if (data.lastDate !== today) {
            data.daily = 0;
            data.lastDate = today;
          }

          this.costTracker = { ...this.costTracker, ...data };
        }
      } else {
        console.log('💻 Desktop mode: localStorage not available, using in-memory tracking');
      }
    } catch (error) {
      console.error('Failed to load cost tracker:', error);
    }
  }

  saveCostTracker() {
    try {
      // Check if localStorage is available (browser environment)
      if (typeof localStorage !== 'undefined') {
        const dataToSave = {
          ...this.costTracker,
          lastDate: new Date().toDateString()
        };
        localStorage.setItem('ai_cost_tracker', JSON.stringify(dataToSave));
      } else {
        console.log('💻 Desktop mode: localStorage not available, skipping save');
      }
    } catch (error) {
      console.error('Failed to save cost tracker:', error);
    }
  }

  async analyzeConstructionProblem(imageBase64, userTier = 'free', location = 'Warszawa') {
    const startTime = Date.now();
    let usedModel = 'unknown';

    try {
      console.log('🧠 Desktop SmartAI Router: Starting multi-model analysis...');

      // STEP 1: Quick screening (FREE - 0.5s)
      const screening = await this.quickScreen(imageBase64);
      console.log('📊 Screening result:', screening);

      // STEP 2: Route to appropriate model
      const selectedModel = this.selectOptimalModel(screening, userTier);
      console.log(`🎯 Selected model: ${selectedModel}`);

      // STEP 3: Execute analysis with selected model
      const analysis = await this.executeAnalysis(selectedModel, imageBase64, screening, location);
      usedModel = selectedModel;

      // STEP 4: Post-process and enhance
      const enhancedResult = await this.enhanceResult(analysis, screening, selectedModel);

      // STEP 5: Record performance metrics
      this.recordSuccess(usedModel, startTime);

      return enhancedResult;

    } catch (error) {
      console.error('Multi-model analysis failed:', error);
      this.recordFailure(usedModel, startTime);

      // Try fallback cascade
      return await this.handleFailureWithCascade(imageBase64, location);
    }
  }

  async quickScreen(imageBase64) {
    try {
      // Desktop optimized screening
      console.log('🔍 Desktop: Quick AI screening...');

      // Use Gemini for quick screening in desktop mode
      const result = await this.callGeminiAPI(imageBase64, this.getScreeningPrompt());

      return this.parseScreeningResult(result);
    } catch (error) {
      console.warn('Screening failed, using heuristic fallback');

      // Heuristic fallback based on image characteristics
      return {
        category: 'general',
        complexity: 'medium',
        urgency: 'medium',
        confidence: 0.5,
        method: 'heuristic'
      };
    }
  }

  getScreeningPrompt() {
    return `
    Przeanalizuj to zdjęcie problemu budowlanego i określ:

    ODPOWIEDZ TYLKO W FORMACIE JSON:
    {
      "category": "electrical/plumbing/structural/cosmetic/general",
      "complexity": "simple/medium/complex",
      "urgency": "low/medium/high",
      "confidence": 0.8
    }

    Kategorie:
    - electrical: instalacje elektryczne
    - plumbing: instalacje wodno-kanalizacyjne
    - structural: konstrukcja, fundamenty, ściany nośne
    - cosmetic: tynki, farby, wykończenia
    - general: inne problemy budowlane
    `;
  }

  parseScreeningResult(result) {
    try {
      // Extract JSON from Gemini response
      const jsonMatch = result.match(/\{[\s\S]*?\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          category: parsed.category || 'general',
          complexity: parsed.complexity || 'medium',
          urgency: parsed.urgency || 'medium',
          confidence: Math.min(1, Math.max(0, parsed.confidence || 0.7)),
          method: 'gemini_screening'
        };
      }
    } catch (error) {
      console.error('Failed to parse screening result:', error);
    }

    // Fallback
    return {
      category: 'general',
      complexity: 'medium',
      urgency: 'medium',
      confidence: 0.5,
      method: 'fallback'
    };
  }

  selectOptimalModel(screening, userTier) {
    const { complexity, category, urgency, confidence } = screening;

    // High confidence in screening results
    if (confidence > 0.8) {
      if (userTier === 'premium' && complexity === 'complex') {
        return this.canAffordModel('imagen') ? 'imagen' : 'gemini';
      }

      if (complexity === 'simple') {
        return 'llava'; // Always FREE for simple problems
      }
    }

    // Critical categories need reliable analysis
    if (category === 'structural' || category === 'electrical') {
      return this.canAffordModel('gemini') ? 'gemini' : 'llava';
    }

    // High urgency cases
    if (urgency === 'high') {
      return this.canAffordModel('gemini') ? 'gemini' : 'llava';
    }

    // Budget management
    if (this.costTracker.daily > this.costTracker.dailyLimit * 0.8) {
      return 'llava'; // Near budget limit, use FREE
    }

    // Premium users get better models
    if (userTier === 'premium') {
      return this.canAffordModel('gemini') ? 'gemini' : 'llava';
    }

    // Default for free users - balance cost and quality
    return this.costTracker.daily < 2 ? 'gemini' : 'llava';
  }

  async executeAnalysis(modelName, imageBase64, screening, location) {
    const startTime = Date.now();

    try {
      let result;

      switch (modelName) {
        case 'imagen':
          console.log('🏆 Using Imagen 3.0 - Premium analysis');
          result = await this.analyzeWithImagen(imageBase64, screening, location);
          this.trackCost(0.01, 'imagen');
          break;

        case 'llava':
          console.log('💚 Using LLaVA simulation - Free analysis');
          result = await this.analyzeWithLLaVA(imageBase64, screening, location);
          // No cost for simulated free model
          break;

        case 'gemini':
          console.log('⚡ Using Gemini - Reliable analysis');
          result = await this.analyzeWithGemini(imageBase64, screening, location);
          this.trackCost(0.003, 'gemini');
          break;

        default:
          throw new Error(`Unknown model: ${modelName}`);
      }

      // Add metadata
      result.metadata = {
        model: modelName,
        processingTime: Date.now() - startTime,
        screening: screening,
        timestamp: new Date().toISOString()
      };

      return result;

    } catch (error) {
      console.error(`${modelName} analysis failed:`, error);
      throw error;
    }
  }

  async analyzeWithGemini(imageBase64, screening, location) {
    const prompt = this.buildAnalysisPrompt(screening, location);
    const result = await this.callGeminiAPI(imageBase64, prompt);
    return this.parseAnalysisResult(result, screening, location);
  }

  async analyzeWithLLaVA(imageBase64, screening, location) {
    // Simulate LLaVA with Polish construction knowledge
    console.log('💚 Simulating LLaVA analysis for desktop...');

    await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate processing time

    return this.getLLaVAMockResponse(screening, location);
  }

  async analyzeWithImagen(imageBase64, screening, location) {
    // For now, use Gemini with premium prompts for Imagen simulation
    const premiumPrompt = this.buildPremiumPrompt(screening, location);
    const result = await this.callGeminiAPI(imageBase64, premiumPrompt);
    return this.parseAnalysisResult(result, screening, location, true);
  }

  async callGeminiAPI(imageBase64, prompt) {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    if (!apiKey) {
      console.log('🔧 No Gemini API key - using mock response');
      // Return different mock based on prompt type
      if (prompt.includes('ODPOWIEDZ TYLKO W FORMACIE JSON') || prompt.includes('category') && prompt.includes('complexity')) {
        return this.getMockScreeningResponse();
      } else {
        return this.getMockGeminiResponse();
      }
    }

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`,
        {
          contents: [{
            parts: [
              { text: prompt },
              {
                inline_data: {
                  mime_type: "image/jpeg",
                  data: imageBase64
                }
              }
            ]
          }],
          generationConfig: {
            temperature: 0.3,
            maxOutputTokens: 1024
          }
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        }
      );

      return response.data.candidates[0].content.parts[0].text;
    } catch (error) {
      console.error('Gemini API error:', error);
      throw new Error('Failed to analyze with Gemini');
    }
  }

  buildAnalysisPrompt(screening, location) {
    return `
    Przeanalizuj ten problem budowlany i podaj szczegółową wycenę w formacie JSON.

    Kontekst:
    - Kategoria: ${screening.category}
    - Złożoność: ${screening.complexity}
    - Lokalizacja: ${location}, Polska

    WYMAGANA STRUKTURA (ZAWSZE JSON):
    {
      "problem_type": "Dokładny opis problemu",
      "description": "Szczegółowy opis (max 200 słów)",
      "severity": "low/medium/high",
      "estimated_cost": {
        "min": liczba_PLN,
        "max": liczba_PLN,
        "confidence": "medium/high"
      },
      "timeline": {
        "days": liczba_dni,
        "description": "Opis czasu"
      },
      "materials": [
        {"name": "Nazwa", "quantity": "Ilość", "cost": liczba_PLN}
      ],
      "labor_cost": liczba_PLN,
      "steps": ["Krok 1", "Krok 2"],
      "difficulty": "easy/medium/hard",
      "urgent": true/false,
      "tips": ["Porada 1", "Porada 2"]
    }

    Użyj realnych cen polskich na rok 2024 dla regionu ${location}.
    `;
  }

  buildPremiumPrompt(screening, location) {
    return this.buildAnalysisPrompt(screening, location) + `

    PREMIUM ANALYSIS - dodaj szczegóły:
    - Dokładne marki materiałów
    - Normy polskie i europejskie
    - Uwagi dotyczące pozwoleń
    - Alternatywne rozwiązania
    - Harmonogram szczegółowy
    `;
  }

  parseAnalysisResult(result, screening, location, isPremium = false) {
    try {
      const jsonMatch = result.match(/\{[\s\S]*\}/);
      const jsonString = jsonMatch ? jsonMatch[0] : result;
      const parsed = JSON.parse(jsonString);

      // Add AI info
      parsed.ai_info = {
        model_used: isPremium ? 'imagen3_simulation' : 'gemini',
        analysis_tier: isPremium ? 'premium' : 'standard',
        polish_market_adjusted: true,
        location: location,
        desktop_version: true
      };

      return parsed;
    } catch (error) {
      console.error('Failed to parse analysis result:', error);
      return this.getFallbackResponse(location);
    }
  }

  getLLaVAMockResponse(screening, location) {
    const locationMultiplier = this.getLocationMultiplier(location);

    return {
      problem_type: "Uszkodzenie tynku na ścianie wewnętrznej",
      description: "Analiza LLaVA: Widoczne pęknięcia i odpryski tynku, prawdopodobnie spowodowane osiadaniem lub zmianami wilgotności. Problem wymaga usunięcia uszkodzonego fragmentu, przygotowania podłoża i ponownego tynkowania.",
      severity: "medium",
      estimated_cost: {
        min: Math.round(400 * locationMultiplier),
        max: Math.round(800 * locationMultiplier),
        confidence: "medium"
      },
      timeline: {
        days: 3,
        description: "2-3 dni robocze"
      },
      materials: [
        { name: "Tynk gipsowy", quantity: "25 kg", cost: Math.round(45 * locationMultiplier) },
        { name: "Grunt", quantity: "5L", cost: Math.round(35 * locationMultiplier) },
        { name: "Farba", quantity: "5L", cost: Math.round(120 * locationMultiplier) }
      ],
      labor_cost: Math.round(500 * locationMultiplier),
      steps: [
        "Oczyszczenie uszkodzonego tynku",
        "Gruntowanie powierzchni",
        "Aplikacja nowego tynku",
        "Szlifowanie i malowanie"
      ],
      difficulty: "medium",
      urgent: false,
      tips: [
        "Sprawdź przyczynę pęknięć przed naprawą",
        "Użyj siatki zbrojącej w narażonych miejscach"
      ],
      ai_info: {
        model_used: 'llava_simulation',
        analysis_tier: 'free',
        polish_market_adjusted: true,
        location: location,
        desktop_version: true
      }
    };
  }

  getMockScreeningResponse() {
    // Mock response for quick screening - returns text that parseScreeningResult can parse
    const mockResponseText = 'Analiza wstępna obrazu pokazuje problem ogólny o średniej złożoności. ' +
            'Kategoria: general, złożoność: medium, pilność: medium. ' +
            'JSON: {"category": "general", "complexity": "medium", "urgency": "medium", "confidence": 0.7}';

    return JSON.stringify({
      text: mockResponseText
    });
  }

  getMockGeminiResponse() {
    return JSON.stringify({
      problem_type: "Problem budowlany (analiza demo)",
      description: "Demo aplikacji desktopowej - rzeczywista analiza wymaga klucza API Gemini.",
      severity: "medium",
      estimated_cost: { min: 500, max: 1200, confidence: "medium" },
      timeline: { days: 3, description: "2-4 dni robocze" },
      materials: [{ name: "Materiały", quantity: "wg potrzeb", cost: 300 }],
      labor_cost: 600,
      steps: ["Ocena przez fachowca", "Wykonanie naprawy"],
      difficulty: "medium",
      urgent: false,
      tips: ["Skonfiguruj klucz API dla pełnej funkcjonalności"]
    });
  }

  getLocationMultiplier(location) {
    const locationMultipliers = {
      'Warszawa': 1.3, 'Kraków': 1.2, 'Gdańsk': 1.15,
      'Wrocław': 1.1, 'Poznań': 1.1, 'Katowice': 1.05,
      'Lublin': 0.95, 'Białystok': 0.9, 'Rzeszów': 0.9
    };
    return locationMultipliers[location] || 1.0;
  }

  canAffordModel(modelName) {
    const costs = { moondream: 0, llava: 0, gemini: 0.003, imagen: 0.01 };
    const cost = costs[modelName] || 0;
    return (this.costTracker.daily + cost) <= this.costTracker.dailyLimit;
  }

  trackCost(amount, modelName) {
    this.costTracker.daily += amount;
    this.costTracker.monthly += amount;
    console.log(`💰 Cost tracked: $${amount} for ${modelName}`);
    this.saveCostTracker();
  }

  recordSuccess(model, startTime) {
    const duration = Date.now() - startTime;
    const perf = this.performance[model];
    if (perf) {
      perf.calls++;
      perf.success++;
      perf.avgTime = ((perf.avgTime * (perf.calls - 1)) + duration) / perf.calls;
    }
  }

  recordFailure(model, startTime) {
    const perf = this.performance[model];
    if (perf) perf.calls++;
  }

  async enhanceResult(analysis, screening, selectedModel) {
    if (screening.urgency === 'high' && !analysis.urgent) {
      analysis.urgent = true;
      analysis.tips = analysis.tips || [];
      analysis.tips.unshift('⚠️ Problem wykryty jako pilny - rozważ szybkie działanie');
    }

    analysis.ai_info = {
      ...analysis.ai_info,
      model_used: selectedModel,
      screening_confidence: screening.confidence,
      analysis_method: 'smart_router_desktop'
    };

    return analysis;
  }

  async handleFailureWithCascade(imageBase64, location) {
    console.log('🔄 Desktop fallback cascade...');

    const fallbacks = ['gemini', 'llava'];

    for (const model of fallbacks) {
      try {
        console.log(`Trying fallback: ${model}`);
        const dummyScreening = { category: 'general', complexity: 'medium', urgency: 'medium', confidence: 0.5 };
        return await this.executeAnalysis(model, imageBase64, dummyScreening, location);
      } catch (error) {
        console.warn(`Fallback ${model} failed:`, error);
      }
    }

    return this.getFallbackResponse(location);
  }

  getFallbackResponse(location = 'Warszawa') {
    const multiplier = this.getLocationMultiplier(location);

    return {
      problem_type: "Problem budowlany wykryty",
      description: "Nie udało się przeanalizować zdjęcia. Skontaktuj się z fachowcem.",
      severity: "medium",
      estimated_cost: { min: Math.round(500 * multiplier), max: Math.round(1200 * multiplier), confidence: "low" },
      timeline: { days: 3, description: "2-4 dni robocze" },
      materials: [{ name: "Materiały", quantity: "wg potrzeb", cost: Math.round(300 * multiplier) }],
      labor_cost: Math.round(600 * multiplier),
      steps: ["Ocena przez fachowca", "Wykonanie naprawy"],
      difficulty: "medium",
      urgent: false,
      tips: ["Zrób lepsze zdjęcie przy dobrym oświetleniu"],
      ai_info: { model_used: 'fallback', desktop_version: true }
    };
  }

  getPerformanceStats() {
    return {
      performance: this.performance,
      costTracker: this.costTracker,
      models: this.models
    };
  }
}

// Export both the class and instance for different use cases
export { SmartAIRouter as SmartAIRouterClass };

// Create singleton instance
const smartAIRouterInstance = new SmartAIRouter();

// ES Module export
export default smartAIRouterInstance;

// CommonJS support for Node.js testing
if (typeof module !== 'undefined' && module.exports) {
  module.exports = smartAIRouterInstance;
  module.exports.SmartAIRouter = SmartAIRouter;
  module.exports.default = smartAIRouterInstance;
}